<script setup>
import FixPanel from "@/components/approval-center/partials/ui/FixPanel.vue";
import FixPanelHeaderTitle from "@/components/approval-center/partials/ui/FixPanelHeaderTitle.vue";
import FixPanelHeader from "@/components/approval-center/partials/ui/FixPanelHeader.vue";
import MoveGroupTransition from "@/components/transitions/MoveGroupTransition.vue";
import FixPanelIcon from "@/components/approval-center/partials/ui/FixPanelIcon.vue";
import FixContent from "@/components/approval-center/partials/ui/FixContent.vue";
import FixText from "@/components/approval-center/partials/ui/FixText.vue";
import KitIcon from "@/components/approval-center/partials/kit/KitIcon.vue";
import useApprovalFlow from "@/composables/approval-center/useApprovalFlow";
import SuccessFlow from "@/components/approval-center/modals/SuccessFlow.vue";
import {ref} from "vue";
import SingleTextDisapprovalFlow from "@/components/approval-center/modals/SingleTextDisapprovalFlow.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import {useApprovals} from "@/composables/approval-center/useApprovals";
import LoadMoreButton from "@/components/approval-center/partials/buttons/LoadMoreButton.vue";
import FixPanelSubText from "@/components/approval-center/partials/ui/FixPanelSubText.vue";
import FixPanelFooter from "@/components/approval-center/partials/ui/FixPanelFooter.vue";

const props = defineProps({
  category: {
    type: String,
    required: true,
  },
});

const {
  showSuccessFlow,
  showDisapprovalFlow,
  handleConfirmMutation,
  handleConfirmForDisapprovalMutation,
  handleClose,
  selectedApproval,
  selectApprovalForApprove,
  selectApprovalForDisapprove,
} = useApprovalFlow();

const { mutate: handleConfirm } = handleConfirmMutation();
const { mutate: handleConfirmForDisapproval } = handleConfirmForDisapprovalMutation();
const descriptionText = ref('');

const {
  approvals,
  isPending,
} = useApprovals(props.category);

const handleDisapprovalFlow = (approval) => {
  descriptionText.value = approval?.approvable?.output?.content;

  selectApprovalForDisapprove(approval);
};
</script>

<template>
  <FixPanel v-if="isPending || approvals.length">
    <FixPanelHeader class="tw-flex-col !tw-items-start">
      <FixPanelHeaderTitle>Google Business Profile Description</FixPanelHeaderTitle>
      <FixPanelSubText>I’ve updated your Google Business Profile description to improve your visibility—please review and approve.</FixPanelSubText>
    </FixPanelHeader>

    <LoadingBlock v-if="isPending" />

    <MoveGroupTransition
      v-else
      class="tw-space-y-8"
    >
      <FixPanel
        v-for="approval in approvals"
        :key="approval.id"
      >
        <div class="tw-flex lg:tw-flex-row tw-flex-col tw-gap-16 tw-justify-between tw-items-stretch">
          <div class="tw-flex tw-flex-col tw-w-full tw-gap-6">
            <FixPanelHeader>
              <FixPanelIcon icon-path="icons/settings-cog" />
              <FixPanelHeaderTitle>Before</FixPanelHeaderTitle>
            </FixPanelHeader>

            <FixContent variant="secondary">
              <FixText>
                {{ approval.approvable.input?.original_content }}
              </FixText>
            </FixContent>
          </div>

          <svg-vue
            icon="icons/arrow-right"
            class="tw-h-8 tw-w-8 tw-text-primary tw-self-center tw-shrink-0"
          />

          <div class="tw-flex tw-flex-col tw-w-full tw-gap-6">
            <FixPanelHeader>
              <FixPanelIcon icon-path="icons/revision" />
              <FixPanelHeaderTitle>Revised</FixPanelHeaderTitle>
            </FixPanelHeader>

            <FixContent class="tw-flex tw-items-start tw-gap-8">
              <KitIcon />

              <FixText>
                {{ approval.approvable?.output?.content }}
              </FixText>
            </FixContent>
          </div>
        </div>

        <FixPanelFooter
          :approval="approval"
          :category="category"
          @approve="selectApprovalForApprove(approval)"
          @revise="handleDisapprovalFlow(approval)"
        />
      </FixPanel>
    </MoveGroupTransition>
    <LoadMoreButton :category="category" />
  </FixPanel>

  <SuccessFlow
    v-if="showSuccessFlow"
    @confirm="handleConfirm({
      approval: selectedApproval,
      category: category,
    })"
    @close="handleClose"
  />

  <SingleTextDisapprovalFlow
    v-if="showDisapprovalFlow"
    v-model="descriptionText"
    @close="handleClose"
    @confirm="handleConfirmForDisapproval({
      approval: selectedApproval,
      newContent: descriptionText,
      category: category
    })"
  >
    <FixPanelHeader>
      <FixPanelIcon icon-path="icons/settings-cog" />
      <FixPanelHeaderTitle>Before</FixPanelHeaderTitle>
    </FixPanelHeader>

    <FixContent variant="disabled">
      <FixText>
        {{ selectedApproval?.approvable.input?.original_content }}
      </FixText>
    </FixContent>

    <template #label>
      <FixPanelIcon icon-path="icons/revision" />
      Description
    </template>
  </SingleTextDisapprovalFlow>
</template>
